import React, { useEffect, useState, useMemo, useRef } from "react";
import ReactECharts from "echarts-for-react";

function Chart({ data, keys, scoreColor = "#477DBA",isLoading = false }: any) {
  const [series, setSeries] = useState<any>([]);
  const [compliantScore, setCompliantScore] = useState<number>(0);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const loaderTimeoutRef = useRef<any>(null);
  const PIE_CENTER: [string, string] = ["50%", "50%"];
  const PIE_RADIUS: [string, string] = ["65%", "100%"];
  const PIECHART_HEIGHT: number = 250;

  const isNoData = series.length === 1 && series[0].name === "No Data";


  // Loader only shows on initial page load, not on filter changes
  useEffect(() => {
    if (isLoading && isInitialLoad) {
      setShowLoader(true);
      if (loaderTimeoutRef.current) {
        clearTimeout(loaderTimeoutRef.current);
      }
    } else if (!isLoading && isInitialLoad) {
      loaderTimeoutRef.current = setTimeout(() => {
        setShowLoader(false);
        setIsInitialLoad(false);
      }, 1000);
    }

    return () => {
      if (loaderTimeoutRef.current) {
        clearTimeout(loaderTimeoutRef.current);
      }
    };
  }, [isLoading, isInitialLoad]);

  const config = useMemo(() => ({
   animation: true,
   animationDuration: 1000,
   animationEasing: 'cubicOut',
   animationDurationUpdate: 500,
   animationEasingUpdate: 'cubicOut',
   tooltip: {
     trigger: "item",
     formatter: (params: any) => {
       if (params.name === "No Data" || params.name === "Background") {
         return null
       }
        const name = params.name.replace(" ", "-");
        // Use the raw value we passed in the series data
        const value = params.data.percent || params.value;
        return `${name}: ${value}%`;
     },
   },
   graphic: {
      type: "group",
      left: "50%",
      top: "50%",
      bounding: "raw",
      children: [
        {
          type: "text",
          style: {
            text: `${compliantScore}%`,
            textAlign: "center",
            textVerticalAlign: "middle",
            fill: scoreColor,
            fontSize: 32,
            fontWeight: "bold",
          },
        },
      ],
    },
   series: [
     {
       name: "Background",
       type: "pie",
       radius: PIE_RADIUS,
       center: PIE_CENTER,
       height: PIECHART_HEIGHT,
       silent: true,
       animation: false,
       label: {
         show: false,
       },
       emphasis: {
         disabled: true,
       },
       data: [
         {
           value: 100,
           name: "Background",
           itemStyle: {
             color: "#D9D9D9"
           },
         },
       ],
       z: 1, // Lower z-index so it appears behind actual data
     },
     // Actual data series - overlays on top of background
     {
       data: series,
       type: "pie",
       smooth: true,
       radius: PIE_RADIUS,
       center: PIE_CENTER,
       height: PIECHART_HEIGHT,
       label: {
         show: false,
       },
       emphasis: {
         disabled: true,
       },
       animation: true,
       animationDuration: 1500,
       animationEasing: 'cubicOut',
       animationDurationUpdate: 1500,
       animationEasingUpdate: 'cubicOut',
       z: 2, // Higher z-index so it appears on top of background
     },
   ],
 }), [series, compliantScore, scoreColor, isNoData]); 

  // useEffect(() => {
  //   if (isLoading || !data || Object.keys(data).length === 0) return;

  //   const compliantValue = data["compliant"] || 0;
  //   const nonCompliantValue = keys
  //     .filter((key: string) => key !== "compliant")
  //     .reduce((sum: number, key: string) => sum + (data[key] || 0), 0);

  //   const totalValue = compliantValue + nonCompliantValue;

  //   if (totalValue === 0) {
  //     setCompliantScore(0);
  //     setSeries([
  //       {
  //         value: 1,
  //         name: "No Data",
  //         itemStyle: { color: "#D9D9D9" },
  //       },
  //     ]);
  //     return;
  //   }

  //   setCompliantScore(Math.round((compliantValue / totalValue) * 100));
  //   setSeries([
  //     {
  //       value: compliantValue,
  //       name: "Compliant",
  //       itemStyle: { color: scoreColor },
  //     },
  //     {
  //       value: nonCompliantValue,
  //       name: "Non Compliant",
  //       itemStyle: { color: "#D9D9D9" },
  //     },
  //   ]);
  // }, [data, keys, scoreColor, isLoading]);

  useEffect(() => {
    if (isLoading || !data || Object.keys(data).length === 0) return;

    const parseValue = (scoreValue: any) => {
        if (!scoreValue) return 0;
        if (typeof scoreValue === 'number') return scoreValue;
        return Number(scoreValue.toString().replace('%', '')) || 0;
    };

    const compliantValue = parseValue(data.compliant);
    const nonCompliantValue = parseValue(data.non_compliant);
    const totalValue = compliantValue + nonCompliantValue;

    if (totalValue === 0) {
      setCompliantScore(0);
      setSeries([{
        value: 1,
        name: "No Data",
        itemStyle: { color: "#D9D9D9" },
      }]);
      return;
    }

    setCompliantScore(compliantValue);
    
    setSeries([
      {
        value: compliantValue,
        name: "Compliant",
        percent: compliantValue, 
        itemStyle: { color: scoreColor },
      },
      {
        value: nonCompliantValue,
        name: "Non Compliant",
        percent: nonCompliantValue, 
        itemStyle: { color: "#D9D9D9" },
      },
    ]);
  }, [data, scoreColor, isLoading]);
  return (
    <div className="d-block">
      {showLoader ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "350px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
      <div className="d-flex justify-content-center gap-4" style={{width: "100%"}}>
        <ReactECharts
          option={config}
          lazyUpdate={true}
          notMerge={false}
          opts={{ renderer: "svg" }}
          style={{ height: 250, width: 250 }}
        />
        <div className="d-flex justify-content-center align-items-center">
          <ul className="p-0 list-unstyled fw-medium" style={{fontSize: "12px"}}>
            {isNoData ? (
                <li className="d-flex align-items-center gap-2">
                  <span
                    className="d-inline-block"
                    style={{
                      height: 14,
                      width: 14,
                      borderRadius: "50%",
                      background: "#d9d9d9",
                    }}
                  />
                  <span>No Data</span>
                </li>
              ) : (
                <>
                  <li className="d-flex align-items-center gap-2 pb-2">
                    <span
                      className="d-inline-block"
                      style={{
                        height: 14,
                        width: 14,
                        borderRadius: "50%",
                        background: scoreColor,
                      }}
                    />
                    <span>Compliant</span>
                  </li>
                  <li className="d-flex align-items-center gap-2">
                    <span
                      className="d-inline-block"
                      style={{
                        height: 14,
                        width: 14,
                        borderRadius: "50%",
                        background: "#d9d9d9",
                      }}
                    />
                    <span>Non Compliant</span>
                  </li>
                </>
              )}
          </ul>
        </div>
      </div>
      )}
    </div>
  );
}

export default Chart;
